import type { Cell, MaxPopupMenu } from '@maxgraph/core';
import type { Store } from '@/graph-store';
import type { ToastCallbacks } from './ContextMenuService';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';

/**
 * Context menu for visual edges that represent simple operations
 * (TRANSFORM, TRANSFER, MEASURE rendered as edges).
 *
 * The cell value contains { operationNodeId, edgeType }.
 * Only collapsed simple-op edges get Edit/Delete.
 * 'flow' edges (complex op I/O) and 'lineage' edges are excluded.
 */
export function buildEdgeMenu(
  menu: MaxPopupMenu,
  cell: Cell,
  store: Store,
  toast?: ToastCallbacks,
): void {
  const val = cell.getValue();
  const edgeId = cell.getId();
  
  const blockedState = {
    isProtocolLocked: store.ui.isProtocolLocked,
    isLifecycleLocked: store.ui.isLifecycleLocked,
    isArchived: store.ui.isArchived,
    isArchivalInProgress: store.ui.isArchivalInProgress,
    runsExist: store.ui.runsExist,
    canEditContent: store.ui.canEditContent,
  };
  
  // Check if this is a link edge (material ↔ material connection)
  if (edgeId) {
    const edge = store.graphData.flowEdges[edgeId];
    if (edge && edge.edgeKind === 'graphsLink') {
      const isLocked = store.ui.isProtocolLocked || store.ui.isArchived || store.ui.isArchivalInProgress || store.ui.runsExist;
      
      menu.addItem('Delete Link', '/icon/delete.svg', () => {
        if (isLocked) {
          toast?.showWarning(getEditBlockedMessage(blockedState));
          return;
        }
        // Use removeLink action from store
        const store_state = store as any;
        if (store_state.removeLink) {
          store_state.removeLink(edgeId);
          toast?.showSuccess('Link Removed Successfully');
        }
      });
      return;
    }
  }

  if (!val || typeof val !== 'object') return;

  const { operationNodeId, edgeType } = val as {
    operationNodeId?: string | null;
    edgeType?: string;
  };

  // Defensive filter: runtime collapsed-op edges always carry operationNodeId.
  // If edgeType is present and not operation, keep excluding it.
  if (!operationNodeId) return;
  if (edgeType && edgeType !== 'operation') return;

  const opNode = store.graphData.operationNodes[operationNodeId];
  if (!opNode) return;

  const isLocked = store.ui.isProtocolLocked || store.ui.isArchived || store.ui.isArchivalInProgress || store.ui.runsExist || store.ui.canEditContent === false;

  menu.addItem('Modify', '/icon/modify.svg', () => {
    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }
    store.openModal({
      kind: 'edit-step',
      payload: { operationId: operationNodeId },
    });
  });

  menu.addItem('Delete', '/icon/delete.svg', () => {
    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }
    store.openModal({
      kind: 'confirm-delete',
      payload: { kind: 'operation', operationId: operationNodeId },
    });
  });
}
